import { Dimensions, PixelRatio, Platform } from 'react-native';

import { browserName, osName, osVersion } from 'react-device-detect';
import {
    getDeviceId,
    getManufacturer,
    getSystemVersion,
    getVersion,
} from 'react-native-device-info';

import { AxiosError, AxiosResponse } from 'axios';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import {
    DEVICE_TYPE,
    addProfileUrl,
    changeUserProfileUrl,
    editProfileUrl,
    getAccountInfo,
    getEntitlementsUrl,
    getFavouritesListsUrl,
    getLogout,
    getPinsStatusUrl,
    getProfileUrl,
    getProfilesUrl,
    getRefreshToken,
    getSessionTolenSignInUrl,
    getSsoSignInUrl,
    getUpdateRatingsThresholdsUrl,
    updateFavouritesListsUrl,
} from 'config/api';

import { SCREEN } from 'router/screens';

import { selectConsentMask } from 'store/config/selectors.ts';
import { deletePlayerSubtitlesFontSize } from 'store/player/actions.ts';
import { onGetChannels } from 'store/programs/saga.ts';
import {
    getRecordingsCache,
    setDeleteActionPinValidationTime,
} from 'store/recordings/actions';
import { selectAppSessionId } from 'store/session/selectors';
import {
    AddProfileResponse,
    AuthData,
    AuthResponseError,
    EntitlementsResponse,
    FavouritesChannelsListsResponse,
    ParentalControlRating,
    PinsStatusResponse,
    Profile,
    ProfilesListsResponse,
    SUSPENSION_TYPES,
    SignInRequestPayload,
} from 'store/user/reducer.types';
import {
    selectAccountId,
    selectClientId,
    selectUserAuthData,
} from 'store/user/selectors';
import { delayRetryRequest } from 'store/utils.ts';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';
import { AppStartUavParams, UavEvents } from 'services/uav/types';
import { logUavAnalytics } from 'services/uav/utils';

import AuthInstance from 'utils/axios/nagra/AuthInstance';
import NagraInstance, {
    setAuthorizationToken,
} from 'utils/axios/nagra/DefaultInstance';
import { response } from 'utils/axios/nagra/interceptors';
import debugLog from 'utils/debugLog.ts';
import encryptSsoPassword from 'utils/encryptSsoPassword';
import isFunction from 'utils/function';
import { isTablet, isWeb } from 'utils/responsiveness';

import { version } from '../../../package.json';
import {
    accountInfoSuccess,
    addProfile,
    addProfileFailure,
    addProfileRequest,
    addProfileSuccess,
    changeUserProfile,
    changeUserProfileFailure,
    changeUserProfileRequest,
    changeUserProfileSuccess,
    createFavouritesChannelsList,
    deleteProfileFailure,
    deleteProfileRequest,
    deleteProfileSuccess,
    editProfile,
    editProfileFailure,
    editProfileRequest,
    editProfileSuccess,
    getEntitlements,
    getEntitlementsFailure,
    getEntitlementsRequest,
    getEntitlementsSuccess,
    getFavouritesChannelsListFailure,
    getFavouritesChannelsListRequest,
    getFavouritesChannelsListSuccess,
    getPinsStatus,
    getPinsStatusFailure,
    getPinsStatusRequest,
    getPinsStatusSuccess,
    getProfileFailure,
    getProfileRequest,
    getProfileSuccess,
    getProfilesList,
    getProfilesListFailure,
    getProfilesListRequest,
    getProfilesListSuccess,
    loginFailure,
    loginRequest,
    loginSilently,
    loginSilentlyFailure,
    loginSilentlySuccess,
    loginSuccess,
    loginWithCredentials,
    loginWithSessionToken,
    logout,
    logoutFailure,
    logoutSuccess,
    resetClientId,
    resetFavouritesChannelsList,
    resetFavouritesChannelsListFailure,
    resetFavouritesChannelsListSuccess,
    setSuspensionType,
    updateFavouritesChannelsList,
    updateFavouritesChannelsListFailure,
    updateFavouritesChannelsListRequest,
    updateFavouritesChannelsListSuccess,
    updateProfileRelatedData,
    updateRatingsThresholds,
    updateRatingsThresholdsFailure,
    updateRatingsThresholdsRequest,
    updateRatingsThresholdsSuccess,
} from './actions';
import {
    ADD_PROFILE,
    CHANGE_USER_PROFILE,
    CHANGE_USER_PROFILE_SUCCESS,
    CREATE_FAVOURITES_CHANNELS_LIST,
    DELETE_FAVOURITES_CHANNELS_LIST,
    DELETE_PROFILE,
    EDIT_PROFILE,
    GET_ENTITLEMENTS,
    GET_FAVOURITES_CHANNELS_LIST,
    GET_PINS_STATUS,
    GET_PROFILES_LIST,
    LOGIN_SILENTLY,
    LOGIN_SUCCESS,
    LOGIN_WITH_CREDENTIALS,
    LOGIN_WITH_SESSION_TOKEN,
    LOGOUT,
    UPDATE_FAVOURITES_CHANNELS_LIST,
    UPDATE_PROFILE_RELATED_DATA,
    UPDATE_PROFILE_RELATED_DATA_FAILURE,
    UPDATE_PROFILE_RELATED_DATA_REQUEST,
    UPDATE_PROFILE_RELATED_DATA_SUCCESS,
    UPDATE_RATINGS_THRESHOLDS,
} from './constants';
import {
    MY_LIST_NAME,
    NO_AUTH_DATA_ERROR,
    appendCreationTimeToTokenData,
    detailsForError,
    isDeviceDeleted,
    isVoluntarySuspension,
} from './utils';

function* onRefreshToken() {
    const authData: AuthData = yield select(selectUserAuthData);

    const refreshTokenResponse: AxiosResponse<AuthData | AuthResponseError> =
        yield call(
            AuthInstance.post,
            getRefreshToken(),
            {},
            {
                headers: {
                    'nv-tenant-id': 'nagra',
                    Authorization: `Bearer ${authData.refresh_token}`,
                },
            },
        );

    yield call(response, refreshTokenResponse);

    yield call(
        setAuthorizationToken,
        (refreshTokenResponse.data as AuthData).access_token,
    );

    const enhancedAuthData: AuthData = yield call(
        appendCreationTimeToTokenData,
        refreshTokenResponse.data as AuthData,
    );

    return enhancedAuthData;
}

function* onGetAccountInfo() {
    try {
        const {
            data: { sharedConsentPublicId },
        } = yield call(NagraInstance.get, getAccountInfo());

        yield put(accountInfoSuccess(sharedConsentPublicId));
    } catch (error: any) {
        debugLog('onGetAccountInfo', 'error', error);

        throw error;
    }
}

export function* onLoginSilently({
    meta: { onSuccess, onFailure },
}: ReturnType<typeof loginSilently>) {
    try {
        const authData: AuthData = yield select(selectUserAuthData);

        if (!authData) {
            yield put(loginSilentlyFailure(NO_AUTH_DATA_ERROR));

            if (isFunction(onFailure)) {
                yield call(onFailure, NO_AUTH_DATA_ERROR);
            }
        } else {
            const enhancedAuthData: AuthData = yield call(onRefreshToken);

            yield call(onGetAccountInfo);

            yield put(loginSilentlySuccess());
            yield put(loginSuccess(enhancedAuthData));

            if (isFunction(onSuccess)) {
                yield call(onSuccess);
            }
        }
    } catch (error: any) {
        if (isVoluntarySuspension(error)) {
            yield put(setSuspensionType(SUSPENSION_TYPES.VOLUNTARY));
        } else if (error?.toJSON?.()?.message !== 'Network Error') {
            yield put(loginSilentlyFailure(error));

            if (isDeviceDeleted(error)) {
                yield put(resetClientId());
            }
        }

        if (isFunction(onFailure)) {
            yield call(onFailure, error);
        }
    }
}

export function* onLoginWithCredentials({
    payload: { email, password },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof loginWithCredentials>) {
    try {
        yield put(loginRequest());

        const clientId: string | undefined = yield select(selectClientId);
        const manufacturer: string = yield call(getManufacturer);
        const systemVersion: string = yield call(getSystemVersion);
        const deviceId: string = yield call(getDeviceId);
        const encryptedPassword: string = yield call(
            encryptSsoPassword,
            password,
        );

        const payload: SignInRequestPayload = {
            parameters: [
                {
                    name: 'username',
                    value: email,
                },
                {
                    name: 'password',
                    value: encryptedPassword,
                },
            ],
        };

        if (clientId) {
            payload.clientId = clientId;
        } else {
            payload.deviceInformation = {
                playoutDeviceClass: Platform.select({
                    web: 'browser',
                    default: 'mobileDevice',
                }),
                device: {
                    screen: {
                        height: PixelRatio.getPixelSizeForLayoutSize(
                            Dimensions.get('screen').height,
                        ),
                        width: PixelRatio.getPixelSizeForLayoutSize(
                            Dimensions.get('screen').width,
                        ),
                    },
                    hardware: {
                        model: Platform.select({
                            web: browserName,
                            default: deviceId,
                        }),
                        manufacturer: Platform.select({
                            web: osName,
                            default: manufacturer,
                        }),
                        type: Platform.select({
                            web: 'Browser',
                            default: isTablet ? 'Tablet' : 'Mobile',
                        }),
                    },
                    OS: {
                        type: Platform.select({
                            ios: 'iOS',
                            android: 'Android',
                            web: osName,
                            default: 'unknown',
                        }),
                        version: Platform.select({
                            web: osVersion,
                            default: systemVersion,
                        }),
                    },
                },
            };
        }

        const ssoResponse: AxiosResponse<AuthData> = yield call(
            AuthInstance.post,
            getSsoSignInUrl(),
            payload,
            {
                headers: {
                    'x-auth-service-id': '4iGSSO',
                    'nv-tenant-id': 'nagra',
                },
            },
        );

        yield call(setAuthorizationToken, ssoResponse.data.access_token);

        const authToken: AuthData = yield call(
            appendCreationTimeToTokenData,
            ssoResponse.data as AuthData,
        );

        yield put(
            loginSuccess(authToken, ssoResponse.data.currentUserProfileId),
        );

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error: any) {
        if (isVoluntarySuspension(error)) {
            yield put(setSuspensionType(SUSPENSION_TYPES.VOLUNTARY));
        } else {
            yield put(loginFailure(detailsForError(error)));

            if (isDeviceDeleted(error)) {
                yield put(resetClientId());
            }
        }

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

export function* onLoginWithSessionToken({
    payload: { sessionToken },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof loginWithSessionToken>) {
    try {
        yield put(loginRequest());

        const clientId: string | undefined = yield select(selectClientId);
        const manufacturer: string = yield call(getManufacturer);
        const systemVersion: string = yield call(getSystemVersion);
        const deviceId: string = yield call(getDeviceId);

        const payload: SignInRequestPayload = {
            parameters: [
                {
                    name: 'sessionToken',
                    value: sessionToken,
                },
            ],
        };

        if (clientId) {
            payload.clientId = clientId;
        } else {
            payload.deviceInformation = {
                playoutDeviceClass: Platform.select({
                    web: 'browser',
                    default: 'mobileDevice',
                }),
                device: {
                    screen: {
                        height: PixelRatio.getPixelSizeForLayoutSize(
                            Dimensions.get('screen').height,
                        ),
                        width: PixelRatio.getPixelSizeForLayoutSize(
                            Dimensions.get('screen').width,
                        ),
                    },
                    hardware: {
                        model: Platform.select({
                            web: browserName,
                            default: deviceId,
                        }),
                        manufacturer: Platform.select({
                            web: osName,
                            default: manufacturer,
                        }),
                        type: Platform.select({
                            web: 'Browser',
                            default: isTablet ? 'Tablet' : 'Mobile',
                        }),
                    },
                    OS: {
                        type: Platform.select({
                            ios: 'iOS',
                            android: 'Android',
                            web: osName,
                            default: 'unknown',
                        }),
                        version: Platform.select({
                            web: osVersion,
                            default: systemVersion,
                        }),
                    },
                },
            };
        }

        const signInResponse: AxiosResponse<AuthData> = yield call(
            AuthInstance.post,
            getSessionTolenSignInUrl(),
            payload,
            {
                headers: {
                    'x-auth-service-id': 'SessionTokenSignOn',
                    'nv-tenant-id': 'nagra',
                },
            },
        );

        yield call(setAuthorizationToken, signInResponse.data.access_token);

        const authToken: AuthData = yield call(
            appendCreationTimeToTokenData,
            signInResponse.data as AuthData,
        );

        yield put(
            loginSuccess(authToken, signInResponse.data.currentUserProfileId),
        );

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error: any) {
        if (isVoluntarySuspension(error)) {
            yield put(setSuspensionType(SUSPENSION_TYPES.VOLUNTARY));
        } else {
            yield put(loginFailure(detailsForError(error)));

            if (isDeviceDeleted(error)) {
                yield put(resetClientId());
            }
        }

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

export function* onLogOut({
    payload: { navigation },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof logout>) {
    try {
        yield call(
            NagraInstance.post,
            getLogout(),
            {},
            {
                headers: {
                    tenantId: 'nagra',
                },
            },
        );

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }

        yield call(navigation.reset, {
            routes: [{ name: SCREEN.LOGIN_SCREEN }],
        });

        yield call(setAuthorizationToken);
        yield put(logoutSuccess());
    } catch (error: any) {
        yield put(logoutFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error);
        }
    }
}

function* requestEntitlements({
    meta: { onSuccess },
}: ReturnType<typeof getEntitlements>) {
    const {
        data: { resourceSet },
    }: AxiosResponse<EntitlementsResponse> = yield call(
        NagraInstance.get,
        getEntitlementsUrl(),
    );

    yield put(getEntitlementsSuccess(resourceSet));

    if (isFunction(onSuccess)) {
        yield call(onSuccess, resourceSet);
    }
}

function* onGetEntitlements(action: ReturnType<typeof getEntitlements>) {
    const {
        meta: { autoRetry, onFailure },
    } = action;

    try {
        yield put(getEntitlementsRequest());
        yield call(requestEntitlements, action);
    } catch (error: any) {
        if (autoRetry && isErrorNeedsRetry(error)) {
            yield fork(retryOnGetEntitlements, action);
        }

        yield put(getEntitlementsFailure(error));

        if (isFunction(onFailure)) {
            yield call(onFailure, error);
        }
    }
}

function* retryOnGetEntitlements(action: ReturnType<typeof getEntitlements>) {
    const {
        retryCount = 5,
        retryDuration = 5 * 60 * 1000,
        onFailure,
    } = action.meta;

    for (let i = 1; i <= retryCount; i += 1) {
        yield call(delayRetryRequest, i, retryCount, retryDuration);

        try {
            yield call(requestEntitlements, action);
            return;
        } catch (error) {
            debugLog(
                'retryOnGetEntitlements',
                `retry #${i} failed with an error`,
                error,
            );

            if (i === retryCount) {
                yield put(getEntitlementsFailure(error as AxiosError));

                if (isFunction(onFailure)) {
                    yield call(onFailure, error as AxiosError);
                }
            }
        }
    }
}

function* requestUpdateRatingsThresholds(rating: ParentalControlRating) {
    const {
        data: { matchedCount },
    }: AxiosResponse<{
        matchedCount: number;
        modifiedCount: number;
    }> = yield call(
        NagraInstance.put,
        getUpdateRatingsThresholdsUrl(),
        {
            HCRS: rating,
        },
        {
            headers: {
                'nv-tenant-id': 'nagra',
            },
        },
    );

    if (matchedCount) {
        yield put(updateRatingsThresholdsSuccess(rating));
    } else {
        yield put(updateRatingsThresholdsFailure(null));
    }
}

function* retryOnUpdateRatingsThresholds({
    payload: { rating },
    meta,
}: ReturnType<typeof updateRatingsThresholds>) {
    const { retryCount = 10, retryDuration = 10 * 60 * 1000, onFailure } = meta;

    for (let i = 1; i <= retryCount; i += 1) {
        yield call(delayRetryRequest, i, retryCount, retryDuration);

        try {
            yield call(requestUpdateRatingsThresholds, rating);
            return;
        } catch (error) {
            debugLog(
                'retryOnUpdateRatingsThresholds',
                `retry #${i} failed with an error`,
                error,
            );

            if (i === retryCount) {
                yield put(updateRatingsThresholdsFailure(error as AxiosError));

                if (isFunction(onFailure)) {
                    yield call(onFailure, error as AxiosError);
                }
            }
        }
    }
}

function* onUpdateRatingsThresholds(
    action: ReturnType<typeof updateRatingsThresholds>,
) {
    const {
        payload: { rating },
        meta: { autoRetry },
    } = action;

    try {
        yield put(updateRatingsThresholdsRequest());
        yield call(requestUpdateRatingsThresholds, rating);
    } catch (error) {
        if (autoRetry && isErrorNeedsRetry(error)) {
            yield fork(retryOnUpdateRatingsThresholds, action);
        }

        yield put(updateRatingsThresholdsFailure(error as AxiosError));
    }
}

function* onGetPinsStatus({ meta }: ReturnType<typeof getPinsStatus>) {
    const onSuccess = meta?.onSuccess;
    const onFailure = meta?.onFailure;

    try {
        yield put(getPinsStatusRequest());

        const {
            data: { resourceSet },
        }: AxiosResponse<PinsStatusResponse> = yield call(
            NagraInstance.get,
            getPinsStatusUrl(),
            {
                headers: { 'nv-tenant-id': 'nagra' },
            },
        );

        yield put(getPinsStatusSuccess(resourceSet));

        if (isFunction(onSuccess)) {
            onSuccess(resourceSet);
        }
    } catch (error) {
        yield put(getPinsStatusFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            onFailure(error);
        }
    }
}

function* onLogAppStartUavEvent() {
    try {
        const accountId: string | undefined = yield select(selectAccountId);
        const deviceId: string | undefined = yield select(selectClientId);
        const appSessionId: string | undefined = yield select(
            selectAppSessionId,
        );

        if (accountId && deviceId && appSessionId) {
            const consentMask = yield select(selectConsentMask);
            const timestamp = new Date().toISOString();
            const params: AppStartUavParams = {
                key: '',
                userId: '',
                accountId,
                deviceId,
                timestamp,
                name: UavEvents.APP_START,
                metadata: {
                    appVersion: Platform.select({
                        web: version,
                        default: getVersion(),
                    }),
                    appName: isWeb ? 'OneTV_Web' : 'OneTV_Mobiles',
                    appSessionId,
                    activityDateTime: timestamp,
                    deviceType: DEVICE_TYPE,
                    consentMask,
                },
            };

            yield call(logUavAnalytics, params);
        }
    } catch (error) {
        /* empty */
    }
}

function* onGetFavouritesChannelsList() {
    try {
        yield put(getFavouritesChannelsListRequest());
        const accountId: string = yield select(selectAccountId);

        const {
            data: { favouritesLists },
        }: AxiosResponse<FavouritesChannelsListsResponse> = yield call(
            NagraInstance.get,
            getFavouritesListsUrl(accountId),
            {
                headers: { 'nv-tenant-id': 'nagra' },
            },
        );

        if (favouritesLists[0] && favouritesLists[0].name !== MY_LIST_NAME) {
            yield put(
                updateFavouritesChannelsList({
                    ...favouritesLists[0],
                    name: MY_LIST_NAME,
                }),
            );
        }

        yield put(getFavouritesChannelsListSuccess(favouritesLists));
    } catch (error) {
        yield put(getFavouritesChannelsListFailure(error as AxiosError));
    }
}

function* onUpdateFavouritesChannelsList({
    payload: { favouritesList },
    meta,
}: ReturnType<typeof updateFavouritesChannelsList>) {
    try {
        yield put(updateFavouritesChannelsListRequest());

        const accountId: string = yield select(selectAccountId);
        const consentMask = yield select(selectConsentMask);

        const {
            data: updatedFavouritesList,
        }: AxiosResponse<
            FavouritesChannelsListsResponse['favouritesLists'][number]
        > = yield call(
            NagraInstance.put,
            updateFavouritesListsUrl(accountId, favouritesList.id),
            {
                ...favouritesList,
                metadata: { ...favouritesList.metadata, consentMask },
            },
            { headers: { 'nv-tenant-id': 'nagra' } },
        );

        yield put(updateFavouritesChannelsListSuccess(updatedFavouritesList));

        if (meta && isFunction(meta?.onSuccess)) {
            yield call(meta.onSuccess);
        }
    } catch (error) {
        yield put(updateFavouritesChannelsListFailure(error as AxiosError));

        if (meta && isFunction(meta?.onFailure)) {
            yield call(meta.onFailure);
        }
    }
}

function* onCreateFavouritesChannelsList({
    payload: { favouritesList, tags },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof createFavouritesChannelsList>) {
    try {
        yield put(updateFavouritesChannelsListRequest());
        const accountId: string = yield select(selectAccountId);
        const clientId: string = yield select(selectClientId);
        const consentMask = yield select(selectConsentMask);

        yield call(
            NagraInstance.post,
            getFavouritesListsUrl(accountId),
            {
                account: accountId,
                name: MY_LIST_NAME,
                userId: clientId,
                metadata: { consentMask, ...favouritesList },
                tags,
            },
            { headers: { 'nv-tenant-id': 'nagra' } },
        );

        yield call(onGetFavouritesChannelsList);

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error) {
        yield put(updateFavouritesChannelsListFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

function* onResetFavouritesChannelsList({
    payload: { favouritesListId },
    meta: { onSuccess },
}: ReturnType<typeof resetFavouritesChannelsList>) {
    try {
        yield put(updateFavouritesChannelsListRequest());
        const accountId: string = yield select(selectAccountId);
        const consentMask = yield select(selectConsentMask);

        yield call(
            NagraInstance.delete,
            updateFavouritesListsUrl(accountId, favouritesListId),
            {
                headers: {
                    'nv-tenant-id': 'nagra',
                },
                data: { metadata: { consentMask } },
            },
        );

        yield put(resetFavouritesChannelsListSuccess());
        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error) {
        yield put(resetFavouritesChannelsListFailure(error as AxiosError));
    }
}

function* onGetProfilesList({
    meta: { onSuccess, onFailure },
}: ReturnType<typeof getProfilesList>) {
    try {
        yield put(getProfilesListRequest());

        const {
            data: { resourceSet },
        }: AxiosResponse<ProfilesListsResponse> = yield call(
            NagraInstance.get,
            getProfilesUrl(),
        );

        if (resourceSet) {
            yield put(getProfilesListSuccess(resourceSet));

            if (isFunction(onSuccess)) {
                yield call(onSuccess, resourceSet);
            }
        }
    } catch (error) {
        yield put(getProfilesListFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

function* onChangeUserProfile({
    payload: { newUserProfileId },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof changeUserProfile>) {
    try {
        yield put(changeUserProfileRequest());

        const { data }: AxiosResponse<AuthData> = yield call(
            NagraInstance.post,
            changeUserProfileUrl(),
            { headers: { 'nv-tenant-id': 'nagra' }, newUserProfileId },
        );

        yield call(setAuthorizationToken, data.access_token);

        const enhancedAuthData: AuthData = yield call(
            appendCreationTimeToTokenData,
            data as AuthData,
        );

        yield put(
            changeUserProfileSuccess(
                {
                    auth: enhancedAuthData,
                    currentUserProfileId: enhancedAuthData.currentUserProfileId,
                },
                { onSuccess },
            ),
        );
    } catch (error) {
        yield put(changeUserProfileFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as AxiosError);
        }
    }
}

function* onAddProfile({
    payload: { name, childProfile, ratingsThresholds, iconName },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof addProfile>) {
    try {
        yield put(addProfileRequest());

        const {
            data: { _id },
        }: AxiosResponse<AddProfileResponse> = yield call(
            NagraInstance.post,
            addProfileUrl(),
            {
                headers: { 'nv-tenant-id': 'nagra' },
                name,
                childProfile,
                ratingsThresholds,
                iconName,
            },
        );

        yield put(addProfileSuccess(_id));

        if (isFunction(onSuccess)) {
            yield call(onSuccess, _id);
        }
    } catch (error) {
        yield put(addProfileFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

function* onEditProfile({
    payload: { name, ratingsThresholds, iconName, id },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof editProfile>) {
    try {
        yield put(editProfileRequest());

        yield call(NagraInstance.put, editProfileUrl(id), {
            name,
            ratingsThresholds,
            iconName,
        });

        yield put(editProfileSuccess());
        yield put(loginSilently());

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error) {
        yield put(editProfileFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

function* onDeleteProfile({
    payload: { id },
    meta: { onSuccess, onFailure },
}: ReturnType<typeof editProfile>) {
    try {
        yield put(deleteProfileRequest());

        yield call(NagraInstance.delete, editProfileUrl(id));

        yield put(deleteProfileSuccess());

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }

        yield put(setDeleteActionPinValidationTime(id, undefined));
        yield put(deletePlayerSubtitlesFontSize(id));
    } catch (error) {
        yield put(deleteProfileFailure(error as AxiosError));

        if (isFunction(onFailure)) {
            yield call(onFailure);
        }
    }
}

function* onProfileSelected({
    payload: { currentUserProfileId },
    meta: { onSuccess },
}: ReturnType<typeof changeUserProfileSuccess>) {
    try {
        yield put(getProfileRequest());

        const { data: profile }: AxiosResponse<Profile> = yield call(
            NagraInstance.get,
            getProfileUrl(currentUserProfileId),
        );

        yield put(getProfileSuccess(profile));
        yield put(getRecordingsCache());

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } catch (error) {
        if (isVoluntarySuspension(error)) {
            yield put(setSuspensionType(SUSPENSION_TYPES.VOLUNTARY));
        }

        yield put(getProfileFailure(error as AxiosError));
    }
}

function* onUpdateProfileRelatedData({
    resolve,
    reject,
}: ReturnType<typeof updateProfileRelatedData>) {
    try {
        yield put({ type: UPDATE_PROFILE_RELATED_DATA_REQUEST });

        yield all([
            call(onGetEntitlements, getEntitlements({ autoRetry: false })),
            call(onGetFavouritesChannelsList),
            call(onGetChannels),
        ]);

        yield put({ type: UPDATE_PROFILE_RELATED_DATA_SUCCESS });

        yield call(resolve, true);
    } catch (error: unknown) {
        yield put({ type: UPDATE_PROFILE_RELATED_DATA_FAILURE, error });

        yield call(reject, error);
    }
}

export default [
    takeLatest(LOGIN_SILENTLY, onLoginSilently),
    takeLatest(LOGIN_WITH_CREDENTIALS, onLoginWithCredentials),
    takeLatest(LOGIN_WITH_SESSION_TOKEN, onLoginWithSessionToken),
    takeLatest(LOGOUT, onLogOut),
    takeLatest(LOGIN_SUCCESS, onLogAppStartUavEvent),
    takeLatest(GET_ENTITLEMENTS, onGetEntitlements),
    takeLatest(UPDATE_RATINGS_THRESHOLDS, onUpdateRatingsThresholds),
    takeLatest(GET_PINS_STATUS, onGetPinsStatus),
    takeLatest(GET_FAVOURITES_CHANNELS_LIST, onGetFavouritesChannelsList),
    takeLatest(UPDATE_FAVOURITES_CHANNELS_LIST, onUpdateFavouritesChannelsList),
    takeLatest(CREATE_FAVOURITES_CHANNELS_LIST, onCreateFavouritesChannelsList),
    takeLatest(DELETE_FAVOURITES_CHANNELS_LIST, onResetFavouritesChannelsList),
    takeLatest(GET_PROFILES_LIST, onGetProfilesList),
    takeLatest(ADD_PROFILE, onAddProfile),
    takeLatest(EDIT_PROFILE, onEditProfile),
    takeLatest(DELETE_PROFILE, onDeleteProfile),
    takeLatest(CHANGE_USER_PROFILE, onChangeUserProfile),
    takeLatest(CHANGE_USER_PROFILE_SUCCESS, onProfileSelected),
    takeLatest(UPDATE_PROFILE_RELATED_DATA, onUpdateProfileRelatedData),
];
